/**
 * The Exec page's story layer:
 *  - EquationStrip: the profit equation as a bar walk — Revenue − variable costs
 *    (make/ship/fees) − marketing − overhead = operating profit. Revenue is
 *    ALWAYS your real number. A cost stage with no input yet renders as a
 *    clearly-labeled EMPTY SLOT plus one plain-language line naming the gap and
 *    linking the fix — never a guessed-in bar, never a "SAMPLE" badge.
 *    (Founder 2026-07-13: "putting 'sample' on something gives absolutely no
 *    benefit — they need to know WHY the information is not complete." Unknown is
 *    never shown as zero, and we never invent a number.)
 *  - InsightLine: an auto-written "what changed and why" sentence under the
 *    headline, naming the biggest product mover vs the prior period (clickable
 *    — it opens the drill that proves it).
 */

import { Link } from "react-router-dom";
import { Explain, moneyFmt } from "./cards.js";
import { cmGapAsk, cmGapBody } from "./CmGapNotice.js";
import { useDemoMode } from "../lib/demo-mode.js";
import type { CmGap } from "../lib/card-requirements.js";
import type { PeriodKey } from "../lib/period.js";

const AMBER = "#d97706";

/** Capability flags the strip needs so it can tell a real gap ("not entered
 *  yet" — the founder can fix it) apart from an input that's simply still
 *  fetching. undefined = capabilities not resolved yet → whole strip shows its
 *  loading skeleton (never a fabricated number). */
export interface EquationCaps {
  contributionMargin: boolean;
  marketingSpend: boolean;
  overhead: boolean;
  cmGap?: CmGap | null;
}

export interface EquationInputs {
  /** Days in the window (null = all-time). Kept for callers; overhead is no longer
   *  scaled here — the server sends it pre-totaled (windowOverhead), clamped to the
   *  real data span, so 'All time' fills in instead of blanking. */
  windowDays: number | null;
  periodLabel: string;
  /** undefined = still loading (show the skeleton, NEVER a placeholder number). */
  revenue?: number;
  /** null = costs still loading OR no costs at all (caps tells which). */
  cm: number | null;
  /** null = spend still loading OR none entered (caps tells which). */
  spend: number | null;
  /** Overhead totaled across the window, clamped to the real data span (server's
   *  windowOverhead). null = still loading OR none entered (caps tells which). On
   *  'All time' this is a real total, not blank. */
  windowOverhead: number | null;
  /** Distinct effective months recorded for overhead. When only one or two exist,
   *  historical profit is still an estimate and the card says so. */
  overheadHistory?: {
    recordedMonths: number;
    sparse: boolean;
    trust: "none" | "manual" | "accounting_pending" | "accounting_approved";
    fallbackMonths: number;
  };
  /** The picker reached before reliable marketing-cost history, so every stage
   *  was narrowed together. Plain-language server disclosure. */
  basisNote?: string | null;
  caps?: EquationCaps;
  /** The ONE shared "% of the way to full break-even" (profit basis) the flagship
   *  Capital-Return-Velocity card derives (server paybackPctToBreakEven). When the
   *  model is COMPLETE and this is a number below 1, the strip adds the founder's
   *  "% to full break-even" line under the waterfall — the SAME value the flagship
   *  shows, never a second computation. */
  pctToBreakEven?: number | null;
}

// A cost stage resolves to exactly one of: a real number; a genuine gap (input
// not entered — a fixable warning); or still loading. (There is no all-time
// "blocked" state anymore — overhead totals across the real data span, so 'All
// time' fills in like every other window.)
type StageState =
  | { kind: "real"; value: number }
  | { kind: "gap" }
  | { kind: "loading" };

interface Notice { text: string; cta?: string; ask?: string; href?: string; tone: "warn" | "info" }

export function EquationStrip(inp: EquationInputs): React.ReactElement {
  const demo = useDemoMode();
  const caps = inp.caps;
  const rev = inp.revenue;

  // Whole-strip loading: revenue not in yet, or capabilities unresolved. We show
  // a skeleton — never the old fake $52,200 pre-first-load revenue.
  const loading = rev === undefined || caps === undefined;

  // PARTIAL costing (founder 2026-07-11): some products sell with no cost inputs,
  // so the true margin is unknowable → the variable-costs stage is a warning, not
  // a number. Demo/test accounts are always fully seeded, so never show it there.
  const cmGap = caps?.cmGap && caps.cmGap.missingProducts > 0 && !demo ? caps.cmGap : null;

  const variable: StageState = !caps
    ? { kind: "loading" }
    : !caps.contributionMargin
      ? { kind: "gap" } // no product costs at all
      : cmGap
        ? { kind: "gap" } // some products still uncosted
        : inp.cm === null || rev === undefined
          ? { kind: "loading" }
          : { kind: "real", value: rev - inp.cm };

  const marketing: StageState = !caps
    ? { kind: "loading" }
    : !caps.marketingSpend
      ? { kind: "gap" }
      : inp.spend === null
        ? { kind: "loading" }
        : { kind: "real", value: inp.spend };

  const overhead: StageState = !caps
    ? { kind: "loading" }
    : !caps.overhead
      ? { kind: "gap" }
      : inp.windowOverhead === null
        ? { kind: "loading" }
        : { kind: "real", value: inp.windowOverhead };

  const allReal =
    rev !== undefined &&
    variable.kind === "real" &&
    marketing.kind === "real" &&
    overhead.kind === "real";

  // One plain-language line per genuine gap (below the chart). CM reuses the
  // exact CmGapNotice copy; every "fix" lands in the SAME costs chat, pre-scoped
  // — the universal "need → chat" pattern (CmGapNotice.tsx), never a new wizard.
  const notices: Notice[] = [];
  if (!loading) {
    if (inp.basisNote) notices.push({ text: inp.basisNote, tone: "info" });
    if (variable.kind === "gap") {
      if (cmGap) {
        notices.push({ text: cmGapBody(cmGap), cta: "Fix it →", ask: cmGapAsk(cmGap), tone: "warn" });
      } else {
        notices.push({
          text: "We can't calculate your contribution margin yet — no product costs are entered.",
          cta: "Add costs →",
          ask: "Let's set up your product costs.",
          tone: "warn",
        });
      }
    }
    if (marketing.kind === "gap") {
      notices.push({
        text: "No marketing spend entered for these dates.",
        cta: "Add it →",
        ask: "Let's add your marketing spend.",
        tone: "warn",
      });
    }
    if (overhead.kind === "gap") {
      if (inp.overheadHistory?.trust === "accounting_pending") {
        notices.push({
          text: "Your accounting expenses have not been confirmed yet, so overhead and operating profit stay locked.",
          cta: "Review expenses →",
          href: "/costs/overhead",
          tone: "warn",
        });
      } else {
        notices.push({
          text: "Overhead isn't entered yet.",
          cta: "Add it →",
          ask: "Let's add your monthly overhead.",
          tone: "warn",
        });
      }
    } else if (overhead.kind === "real" && inp.overheadHistory?.trust === "accounting_approved" && inp.overheadHistory.fallbackMonths > 0) {
      const months = inp.overheadHistory.fallbackMonths;
      notices.push({
        text: `${months} month${months === 1 ? " uses" : "s use"} your approved planning amount because a complete accounting report is missing.`,
        tone: "info",
      });
    } else if (overhead.kind === "real" && inp.overheadHistory?.sparse) {
      const months = inp.overheadHistory.recordedMonths;
      notices.push({
        text: `We only have ${months} month${months === 1 ? "" : "s"} of overhead history. Earlier months use your earliest recorded amount, so past operating profit is still an estimate.`,
        tone: "info",
      });
    }
  }

  // The short reason on the (unavailable) operating-profit slot — a profit number
  // missing a cost isn't a profit number, so we say what it's waiting on.
  const need: string[] = [];
  if (variable.kind === "gap") need.push("costs");
  if (marketing.kind === "gap") need.push("spend");
  if (overhead.kind === "gap") need.push("overhead");
  const profitReason =
    need.length === 0
      ? "…"
      : need.length === 1
        ? `needs ${need[0]}`
        : `needs ${need.slice(0, -1).join(", ")} & ${need[need.length - 1]}`;

  const steps: BridgeStep[] = allReal
    ? [
        { label: "Net revenue", value: rev!, kind: "total" },
        { label: "Variable costs", value: (variable as { value: number }).value, kind: "down" },
        { label: "Marketing", value: (marketing as { value: number }).value, kind: "down" },
        { label: "Overhead", value: (overhead as { value: number }).value, kind: "down" },
        {
          label: "Operating profit",
          value:
            rev! -
            (variable as { value: number }).value -
            (marketing as { value: number }).value -
            (overhead as { value: number }).value,
          kind: "total",
        },
      ]
    : [];

  return (
    <div style={strip}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ ...stripTitle, display: "inline-flex", alignItems: "center", gap: 5 }}>
          Your profit model · {inp.periodLabel}
          <Explain title="Your profit model" lines={[
            "<b>What it is:</b> The walk from revenue down to operating profit — each bar is a cost stepping the total down: variable costs (make/ship/fees), marketing, then overhead.",
            "<b>How it's calculated:</b> Net revenue for the window − variable costs (revenue minus contribution margin) − marketing spend − overhead totaled across the same dates.",
            "<b>Example (sample data · year-to-date):</b> $1.16M revenue − $546K variable − $278K marketing − $132K overhead = $200K operating profit.",
            "<b>Note:</b> Blended, since marketing and overhead aren't tracked per product. If your dates reach back before marketing costs were tracked, every stage starts at the first covered month. Missing spend never counts as $0. A cost you have not set up yet shows as an empty, labeled slot.",
          ]} />
        </div>
      </div>

      {loading ? (
        <LoadingBridge />
      ) : allReal ? (
        <ProfitBridge steps={steps} />
      ) : (
        <PartialBridge
          revenue={rev!}
          variable={variable}
          marketing={marketing}
          overhead={overhead}
          cmPartial={cmGap !== null}
          profitReason={profitReason}
        />
      )}

      {notices.length > 0 && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {notices.map((nt, i) => (
            <div key={i} role={nt.tone === "warn" ? "status" : undefined} style={noticeBox(nt.tone)}>
              {nt.tone === "warn" && <span aria-hidden="true" style={{ marginRight: 5 }}>⚠️</span>}
              {nt.text}
              {nt.cta && (nt.ask || nt.href) && (
                <>
                  {" "}
                  <Link
                    to={nt.href ?? "/costs"}
                    state={nt.ask ? { costsAsk: nt.ask } : undefined}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: AMBER, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}
                  >
                    {nt.cta}
                  </Link>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Founder's addition (2026-07-13): when the COMPLETE model's window hasn't
          broken even, show the SAME "% of the way to full break-even" the flagship
          Capital-Return-Velocity card derives — one shared value, never two. */}
      {allReal && typeof inp.pctToBreakEven === "number" && inp.pctToBreakEven < 1 && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--body)", lineHeight: 1.5 }}>
          <b className="dd-num" style={{ color: "var(--accent)" }}>{Math.round(inp.pctToBreakEven * 100)}%</b> of the way to full break-even in this window — its customers haven&rsquo;t fully paid back what they cost yet. See <b>Capital return velocity</b> for the journey.
        </div>
      )}
    </div>
  );
}

interface BridgeStep { label: string; value: number; kind: "total" | "down" }

/** Profit-bridge waterfall (founder 2026-06-13, research-driven redesign): the
 *  signature finance chart, shown ONLY when every stage is real. Total bars
 *  (revenue, operating profit) rise from zero; cost steps float down from the
 *  running total. Responsive SVG, themed via tokens, dark-mode safe. */
function ProfitBridge({ steps }: { steps: BridgeStep[] }): React.ReactElement {
  const W = 660, H = 240;
  const PAD = { l: 6, r: 6, t: 26, b: 34 };
  const iw = W - PAD.l - PAD.r;
  const ihTop = PAD.t;
  const n = steps.length;
  const slot = iw / n;
  const bw = Math.min(96, slot * 0.6);
  // Each block's share of net revenue (founder 2026-06-16: "show what percent
  // each block makes up — variable costs ~40% since CM is ~60%"). Net revenue
  // is the base (100%); operating profit reads as the net margin.
  const base = steps[0]?.value || 1;

  let run = 0;
  const bars = steps.map((s) => {
    let lo: number, hi: number;
    if (s.kind === "total") { lo = Math.min(0, s.value); hi = Math.max(0, s.value); run = s.value; }
    else { hi = run; lo = run - s.value; run = lo; }
    return { ...s, lo, hi };
  });
  const max = Math.max(0, ...bars.map((b) => b.hi));
  const min = Math.min(0, ...bars.map((b) => b.lo));
  const span = max - min || 1;
  const innerH = H - PAD.t - PAD.b;
  const y = (v: number): number => ihTop + innerH - ((v - min) / span) * innerH;
  const cx = (i: number): number => PAD.l + i * slot + slot / 2;

  const colorFor = (b: (typeof bars)[number]): string =>
    b.kind === "down" ? "var(--bad)" : b.value < 0 ? "var(--bad)" : "var(--good)";

  return (
    <div style={{ position: "relative", marginTop: 8 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label="Profit bridge waterfall from net revenue down through variable costs, marketing and overhead to operating profit">
        <line x1={PAD.l} y1={y(0)} x2={W - PAD.r} y2={y(0)} stroke="var(--grid)" />
        {bars.map((b, i) => {
          const top = y(b.hi), bot = y(b.lo);
          const h = Math.max(2, bot - top);
          const isDown = b.kind === "down";
          return (
            <g key={b.label}>
              <rect x={cx(i) - bw / 2} y={top} width={bw} height={h} rx={3} fill={colorFor(b)} opacity={isDown ? 0.82 : 1} />
              <text x={cx(i)} y={top - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--num)" className="dd-num">
                {isDown ? "−" : ""}{moneyFmt(b.value)}
              </text>
              <text x={cx(i)} y={H - 16} textAnchor="middle" fontSize={10} fill="var(--body-faint)">{b.label}</text>
              <text x={cx(i)} y={H - 4} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="var(--faint)" className="dd-num">{Math.round((b.value / base) * 100)}%</text>
            </g>
          );
        })}
        {/* step connectors: dashed line linking each bar's exit level to the next bar's entry */}
        {bars.slice(0, -1).map((b, i) => {
          const next = bars[i + 1]!;
          const level = next.kind === "down" ? next.hi : (b.kind === "total" ? b.value : b.lo);
          return <line key={`c${i}`} x1={cx(i) + bw / 2} y1={y(b.kind === "down" ? b.lo : b.value)} x2={cx(i + 1) - bw / 2} y2={y(level)} stroke="var(--body-faint)" strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />;
        })}
      </svg>
    </div>
  );
}

/** The degraded profit model (founder 2026-07-13): revenue is real and every
 *  cost we KNOW is drawn to scale against it, but any stage without an input is
 *  an explicitly-empty hatched slot with no number — and operating profit stays
 *  unavailable (with its short reason) because a profit built on a missing cost
 *  is a wrong number. We deliberately do NOT draw the cumulative running total
 *  here: with a piece missing there is no honest place to step it down to. */
function PartialBridge({
  revenue,
  variable,
  marketing,
  overhead,
  cmPartial,
  profitReason,
}: {
  revenue: number;
  variable: StageState;
  marketing: StageState;
  overhead: StageState;
  cmPartial: boolean;
  profitReason: string;
}): React.ReactElement {
  const W = 660, H = 240;
  const PAD = { l: 6, r: 6, t: 26, b: 34 };
  const iw = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const n = 5;
  const slot = iw / n;
  const bw = Math.min(96, slot * 0.6);
  const baseline = PAD.t + innerH;
  const cx = (i: number): number => PAD.l + i * slot + slot / 2;
  const topFor = (f: number): number => baseline - Math.max(0, Math.min(1, f)) * innerH;
  const GHOST_F = 0.45; // neutral placeholder height for an unavailable slot

  const cols: { label: string; state: StageState; total?: boolean; cost?: boolean; profit?: boolean }[] = [
    { label: "Net revenue", state: { kind: "real", value: revenue }, total: true },
    { label: "Variable costs", state: variable, cost: true },
    { label: "Marketing", state: marketing, cost: true },
    { label: "Overhead", state: overhead, cost: true },
    { label: "Operating profit", state: { kind: "gap" }, total: true, profit: true },
  ];

  return (
    <div style={{ position: "relative", marginTop: 8 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label="Profit model — revenue is real; cost stages without an input show as empty slots">
        <defs>
          <pattern id="pmw-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="7" stroke="var(--border)" strokeWidth={1.4} />
          </pattern>
        </defs>
        <line x1={PAD.l} y1={baseline} x2={W - PAD.r} y2={baseline} stroke="var(--grid)" />
        {cols.map((c, i) => {
          const st = c.state;
          if (st.kind === "real") {
            const f = c.total ? 1 : st.value / (revenue || 1);
            const top = topFor(f);
            const h = Math.max(2, baseline - top);
            return (
              <g key={c.label}>
                <rect x={cx(i) - bw / 2} y={top} width={bw} height={h} rx={3} fill={c.total ? "var(--good)" : "var(--bad)"} opacity={c.total ? 1 : 0.82} />
                <text x={cx(i)} y={top - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--num)" className="dd-num">
                  {c.cost ? "−" : ""}{moneyFmt(st.value)}
                </text>
                <text x={cx(i)} y={H - 16} textAnchor="middle" fontSize={10} fill="var(--body-faint)">{c.label}</text>
                <text x={cx(i)} y={H - 4} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="var(--faint)" className="dd-num">{Math.round((st.value / (revenue || 1)) * 100)}%</text>
              </g>
            );
          }
          // unavailable slot — hatched + dashed outline; a loading slot is a muted
          // solid pulse. No number in either case.
          const isLoading = st.kind === "loading";
          const top = topFor(GHOST_F);
          const h = baseline - top;
          const sub = c.profit
            ? profitReason
            : isLoading
              ? "Loading…"
              : c.cost && c.label === "Variable costs" && cmPartial
                ? "Some costs missing"
                  : "Not entered";
          return (
            <g key={c.label}>
              <rect
                x={cx(i) - bw / 2}
                y={top}
                width={bw}
                height={h}
                rx={3}
                fill={isLoading ? "var(--surface-2)" : "url(#pmw-hatch)"}
                stroke="var(--border)"
                strokeDasharray={isLoading ? undefined : "3 3"}
                opacity={isLoading ? 0.7 : 1}
              >
                {isLoading && <animate attributeName="opacity" values="0.35;0.75;0.35" dur="1.4s" repeatCount="indefinite" />}
              </rect>
              <text x={cx(i)} y={top - 6} textAnchor="middle" fontSize={12} fontWeight={700} fill="var(--faint)">—</text>
              <text x={cx(i)} y={H - 16} textAnchor="middle" fontSize={10} fill="var(--body-faint)">{c.label}</text>
              <text x={cx(i)} y={H - 4} textAnchor="middle" fontSize={9.5} fontWeight={600} fill="var(--faint)">{sub}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Loading skeleton — muted, numberless, gently pulsing. Replaces the old
 *  fabricated $52,200 pre-first-load bars: a founder must never see an invented
 *  number, not even for a frame. */
function LoadingBridge(): React.ReactElement {
  const W = 660, H = 240;
  const PAD = { l: 6, r: 6, t: 26, b: 34 };
  const iw = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const n = 5;
  const slot = iw / n;
  const bw = Math.min(96, slot * 0.6);
  const baseline = PAD.t + innerH;
  const cx = (i: number): number => PAD.l + i * slot + slot / 2;
  const fr = [1, 0.55, 0.35, 0.28, 0.5];
  return (
    <div style={{ marginTop: 8 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} role="img" aria-label="Loading your profit model">
        <line x1={PAD.l} y1={baseline} x2={W - PAD.r} y2={baseline} stroke="var(--grid)" />
        {fr.map((f, i) => {
          const top = baseline - f * innerH;
          return (
            <rect key={i} x={cx(i) - bw / 2} y={top} width={bw} height={baseline - top} rx={3} fill="var(--surface-2)" opacity={0.7}>
              <animate attributeName="opacity" values="0.35;0.7;0.35" dur="1.4s" begin={`${i * 0.12}s`} repeatCount="indefinite" />
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

// ----------------------------------------------------------------------------

export interface MoverRow { key: string; value: number; prev?: number }

// The "vs the ___" comparison phrase, keyed EXPLICITLY by window instead of
// string-munging the label (which produced "vs the prior year to date" / "vs
// the prior all time"). The prior window is always the immediately-preceding
// equal-length block (resolvePeriod), so ytd/all read as "prior period", not
// "same period last year" (founder 2026-07-07).
const PRIOR_LABEL: Record<PeriodKey, string> = {
  "30d": "prior 30 days",
  "90d": "prior 90 days",
  "365d": "prior year",
  ytd: "prior period",
  all: "prior period",
  custom: "prior period",
};

export function InsightLine({
  revenuePct,
  periodKey,
  movers,
  onMoverClick,
}: {
  revenuePct: number | null | undefined;
  periodKey: PeriodKey;
  movers: MoverRow[] | undefined;
  onMoverClick?: (product: string) => void;
}): React.ReactElement | null {
  if (revenuePct === null || revenuePct === undefined) return null;
  const up = revenuePct >= 0;

  // Biggest absolute swing vs prior period (compare rows carry prev).
  let top: { key: string; delta: number } | null = null;
  for (const r of movers ?? []) {
    if (r.prev === undefined) continue;
    const delta = r.value - r.prev;
    if (!top || Math.abs(delta) > Math.abs(top.delta)) top = { key: r.key, delta };
  }

  return (
    <p style={{ margin: "10px 0 0", fontSize: 13.5, color: "var(--body)", lineHeight: 1.55, maxWidth: 860 }}>
      Revenue is{" "}
      <b style={{ color: up ? "var(--good)" : "var(--bad)" }}>
        {up ? "up" : "down"} {Math.abs(revenuePct * 100).toFixed(1)}%
      </b>{" "}
      vs the {PRIOR_LABEL[periodKey]}
      {top && Math.abs(top.delta) >= 1 ? (
        <>
          {" — biggest mover: "}
          <button onClick={() => onMoverClick?.(top!.key)} style={moverBtn} title="Drill into this product">
            {top.key}
          </button>{" "}
          <b className="dd-num" style={{ color: top.delta >= 0 ? "var(--good)" : "var(--bad)" }}>
            ({top.delta >= 0 ? "+" : "−"}{moneyFmt(Math.abs(top.delta))})
          </b>
          .
        </>
      ) : (
        "."
      )}
    </p>
  );
}

const strip: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-card)",
  padding: "14px 18px",
  marginTop: 18,
};
const stripTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--accent)",
};
const moverBtn: React.CSSProperties = {
  background: "transparent", border: "none", padding: 0, cursor: "pointer",
  color: "var(--accent)", fontSize: 13.5, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3,
};

/** Compact gap/info line under the degraded chart. warn = amber (a fixable
 *  gap, matching CmGapNotice); info = neutral (a window choice, nothing wrong). */
function noticeBox(tone: "warn" | "info"): React.CSSProperties {
  return {
    borderRadius: 10,
    border: tone === "warn" ? `1px solid color-mix(in srgb, ${AMBER} 45%, var(--border))` : "1px solid var(--border)",
    background: tone === "warn" ? `color-mix(in srgb, ${AMBER} 8%, transparent)` : "var(--surface-2)",
    padding: "9px 12px",
    fontSize: 12.5,
    color: "var(--body)",
    lineHeight: 1.5,
  };
}
